// @ts-nocheck
/**
 * DingTalk JSAPI 工具类
 * 按照钉钉官方免密登录流程实现
 */

// 声明全局 dd 对象
declare const dd: any

// 扩展 Window 接口
declare global {
  interface Window {
    __dingTalkDebugInfo: any[]
  }
}


class DingTalkUtil {
  private isReady: boolean = false

  constructor() {
    this.isReady = false
    // 只在钉钉环境中才初始化
    if (this.isDingTalkEnv()) {
      this.init()
    }
  }

  /**
   * 添加调试信息
   * @param {string} level - 日志级别：success, error, warning, info
   * @param {string} message - 日志消息
   * @param {any} data - 附加数据
   */
  addDebugInfo(level, message, data = null) {
    // 添加到全局调试信息数组
    if (window.__dingTalkDebugInfo && Array.isArray(window.__dingTalkDebugInfo)) {
      const info = {
        level,
        message,
        data,
        timestamp: new Date().toLocaleString('zh-CN')
      }
      window.__dingTalkDebugInfo.push(info)
    }

    // 同时输出到控制台
    const levelMap = {
      success: 'log',
      error: 'error',
      warning: 'warn',
      info: 'log'
    }
    const consoleMethod = levelMap[level] || 'log'
    
    if (data) {
      console[consoleMethod](message, data)
    } else {
      console[consoleMethod](message)
    }
  }

  /**
   * 检测是否为钉钉环境
   */
  isDingTalkEnv() {
    return typeof dd !== 'undefined'
  }

  /**
   * 检测是否为PC端
   */
  isPcClient() {
    if (!this.isDingTalkEnv()) return false
    const ua = window.navigator.userAgent.toLowerCase()
    return ua.indexOf('mac') > -1 || ua.indexOf('windows') > -1
  }

  /**
   * 检测是否为Android设备
   */
  isAndroid() {
    const ua = window.navigator.userAgent.toLowerCase()
    return ua.indexOf('android') > -1
  }

  /**
   * 检测是否为iOS设备
   */
  isIOS() {
    const ua = window.navigator.userAgent.toLowerCase()
    return ua.indexOf('iphone') > -1 || ua.indexOf('ipad') > -1
  }

  /**
   * 初始化钉钉 JSAPI
   * 针对安卓和iOS的不同处理方式
   */
  async init() {
    // 检查是否在钉钉环境中
    if (!this.isDingTalkEnv()) {
      this.addDebugInfo('warning', '[DD] 当前不在钉钉环境中，跳过初始化')
      return { success: false, error: 'dd对象不存在' }
    }

    const isAndroid = this.isAndroid()
    const isIOS = this.isIOS()
    const isPc = this.isPcClient()

    this.addDebugInfo('info', '[DD] 设备类型检测', {
      isAndroid,
      isIOS,
      isPc,
      userAgent: window.navigator.userAgent
    })

    // 检查 dd.ready 是否存在
    if (typeof dd.ready !== 'function') {
      this.addDebugInfo('error', '[DD] dd.ready 不是函数', {
        ddType: typeof dd,
        ddKeys: Object.keys(dd).slice(0, 20)
      })
      return {
        success: false,
        error: 'dd.ready 不是函数'
      }
    }

    try {
      // PC端特殊处理
      if (isPc) {
        this.addDebugInfo('info', '[DD] 检测到PC端环境，使用PC端初始化方式')
        
        try {
          // PC端可能不需要调用 dd.ready()
          this.isReady = true
          this.addDebugInfo('success', '[DD] ✅ PC端钉钉 JSAPI 初始化完成')
          return { success: true, attempts: 1, pcMode: true }
        } catch (error) {
          this.addDebugInfo('warning', '[DD] PC端初始化失败，尝试调用 dd.ready()')
        }
      }
      
      // 安卓端特殊处理：使用回调方式而不是Promise
      if (isAndroid) {
        this.addDebugInfo('info', '[DD] 🤖 检测到Android设备，使用回调方式初始化')
        
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            this.addDebugInfo('error', '[DD] ❌ Android初始化超时(5秒)')
            reject(new Error('Android dd.ready 超时'))
          }, 5000)

          try {
            // 安卓端使用回调方式
            dd.ready(function() {
              clearTimeout(timeout)
              this.isReady = true
              this.addDebugInfo('success', '[DD] ✅ Android钉钉 JSAPI 初始化成功(回调方式)')
              resolve({ success: true, attempts: 1, platform: 'android' })
            }.bind(this))
          } catch (error) {
            clearTimeout(timeout)
            this.addDebugInfo('error', '[DD] ❌ Android dd.ready调用异常', {
              errorName: error.name,
              errorMessage: error.message,
              errorStack: error.stack ? error.stack.substring(0, 200) : 'no stack'
            })
            
            // 安卓端即使dd.ready失败，也尝试直接标记为ready
            this.addDebugInfo('warning', '[DD] ⚠️ 尝试跳过dd.ready，直接标记为ready')
            this.isReady = true
            resolve({ success: true, attempts: 1, platform: 'android', skipReady: true })
          }
        })
      }
      
      // iOS端：使用Promise方式
      if (isIOS) {
        this.addDebugInfo('info', '[DD] 🍎 检测到iOS设备，使用Promise方式初始化')
        
        // iOS端可以使用await
        await dd.ready()
        
        this.isReady = true
        this.addDebugInfo('success', '[DD] ✅ iOS钉钉 JSAPI 初始化成功')
        return { success: true, attempts: 1, platform: 'ios' }
      }

      // 其他情况：尝试通用方式
      this.addDebugInfo('info', '[DD] 使用通用方式初始化')
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.addDebugInfo('warning', '[DD] ⚠️ 通用初始化超时，直接标记为ready')
          this.isReady = true
          resolve({ success: true, attempts: 1, platform: 'unknown', skipReady: true })
        }, 3000)

        try {
          dd.ready(function() {
            clearTimeout(timeout)
            this.isReady = true
            this.addDebugInfo('success', '[DD] ✅ 钉钉 JSAPI 初始化成功(通用方式)')
            resolve({ success: true, attempts: 1, platform: 'unknown' })
          }.bind(this))
        } catch (error) {
          clearTimeout(timeout)
          this.addDebugInfo('warning', '[DD] ⚠️ 通用初始化失败，直接标记为ready')
          this.isReady = true
          resolve({ success: true, attempts: 1, platform: 'unknown', skipReady: true })
        }
      })
      
    } catch (error) {
      this.addDebugInfo('error', '[DD] ❌ 钉钉 JSAPI 初始化异常', {
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack ? error.stack.substring(0, 300) : 'no stack'
      })
      
      // 最后的兜底：即使失败也标记为ready，让后续流程继续
      this.addDebugInfo('warning', '[DD] ⚠️ 初始化失败，但仍尝试继续执行')
      this.isReady = true
      
      return {
        success: true, // 改为true，让流程继续
        error: {
          name: error.name,
          message: error.message
        },
        fallback: true
      }
    }
  }

  /**
   * 获取免登授权码
   * 
   * 钉钉免密登录完整流程说明：
   * 
   * 【前端】步骤一：获取免登授权码 (本方法)
   *   使用 dd.runtime.permission.requestAuthCode() 获取授权码
   * 
   * 【后端】步骤二：获取 access_token
   *   GET https://oapi.dingtalk.com/gettoken
   *   参数：{ appkey, appsecret }
   * 
   * 【后端】步骤三：获取用户 userid
   *   POST https://oapi.dingtalk.com/topapi/v2/user/get?access_token=ACCESS_TOKEN
   *   使用步骤一获取的 code 和步骤二的 access_token
   * 
   * 注意：步骤二和三由后端 API (/auth/dingtalk/login) 实现
   */
  /**
   * 获取免登授权码
   * 
   * @param corpId - 可选，指定使用的组织 ID。不传则使用默认主组织
   */
  async getAuthCode(corpId?: string) {
    if (!this.isDingTalkEnv()) {
      throw new Error('[DD] 当前不在钉钉环境中')
    }

    // 组织配置：主组织和备用组织
    const CORP_IDS = {
      primary: 'ding22edd3ac4f8e7fa8a1320dcb25e91351',
      backup: 'dingb78698784bbfd173ee0f45d8e4f7c288',
    }
    
    // 使用传入的 corpId 或默认主组织
    const useCorpId = corpId || process.env.VUE_APP_CORP_ID || CORP_IDS.primary
    this.addDebugInfo('info', '[DD] 📞 开始获取免登授权码', { corpId: useCorpId })

    return new Promise((resolve, reject) => {
      // 检查 dd.runtime.permission.requestAuthCode 是否可用
      if (!dd.runtime || !dd.runtime.permission || typeof dd.runtime.permission.requestAuthCode !== 'function') {
        const error = {
          name: 'DD_API_NOT_AVAILABLE',
          message: 'dd.runtime.permission.requestAuthCode 不可用',
          details: {
            hasRuntime: !!dd.runtime,
            hasPermission: !!(dd.runtime && dd.runtime.permission),
            hasRequestAuthCode: !!(dd.runtime && dd.runtime.permission && typeof dd.runtime.permission.requestAuthCode === 'function')
          }
        }
        this.addDebugInfo('error', '[DD] ❌ API 不可用', error)
        reject(error)
        return
      }

      // 钉钉官方标准流程：步骤一 - 获取免登授权码
      // 参考：https://open.dingtalk.com/document/orgapp-client/obtain-the-identity-verification-code-in-a-microapp
      dd.runtime.permission.requestAuthCode({
        corpId: useCorpId,
        onSuccess: (info) => {
          this.addDebugInfo('success', '[DD] ✅ 授权码获取成功', info)
          
          if (info && info.code) {
            resolve(info.code)
          } else {
            const error = {
              name: 'DD_INVALID_CODE_RESPONSE',
              message: '授权码返回格式错误',
              details: { response: info }
            }
            this.addDebugInfo('error', '[DD] ❌ 授权码格式错误', error)
            reject(error)
          }
        },
        onFail: (err) => {
          const error = {
            name: 'DD_GET_AUTH_CODE_FAILED',
            message: err?.message || '获取授权码失败',
            details: { error: err }
          }
          this.addDebugInfo('error', '[DD] ❌ 授权码获取失败', error)
          reject(error)
        }
      })
    })
  }

  /**
   * 获取用户信息
   * 这个方法是 getAuthCode 的别名，用于兼容旧代码
   * @param corpId - 可选，指定使用的组织 ID
   */
  async getUserInfo(corpId?: string) {
    return this.getAuthCode(corpId)
  }

  /**
   * 获取用户信息
   * 这个方法是 getAuthCode 的别名，用于兼容旧代码
   */
  async getUserInfo() {
    return this.getAuthCode()
  }

  /**
   * 设置导航栏标题
   */
  setNavBar(title = '') {
    if (!this.isDingTalkEnv()) {
      this.addDebugInfo('info', '[DD] 非钉钉环境，跳过设置导航栏')
      return
    }

    if (!this.isReady) {
      this.addDebugInfo('warning', '[DD] 钉钉 JSAPI 未就绪')
      return
    }

    if (!dd.biz || !dd.biz.navigation || typeof dd.biz.navigation.setTitle !== 'function') {
      this.addDebugInfo('warning', '[DD] dd.biz.navigation.setTitle 不可用')
      return
    }

    dd.biz.navigation.setTitle({
      title: title,
      onSuccess: () => {
        this.addDebugInfo('success', '[DD] ✅ 设置标题成功', { title })
      },
      onFail: (err) => {
        this.addDebugInfo('error', '[DD] ❌ 设置标题失败', err)
      }
    })
  }

  /**
   * 设置右上角菜单
   */
  setRightMenu(menus = []) {
    if (!this.isDingTalkEnv()) {
      return
    }

    if (!this.isReady) {
      this.addDebugInfo('warning', '[DD] 钉钉 JSAPI 未就绪')
      return
    }

    if (!dd.biz || !dd.biz.navigation || typeof dd.biz.navigation.setMenu !== 'function') {
      this.addDebugInfo('warning', '[DD] dd.biz.navigation.setMenu 不可用')
      return
    }

    dd.biz.navigation.setMenu({
      items: menus,
      onSuccess: (result) => {
        this.addDebugInfo('info', '[DD] 菜单点击', result)
      },
      onFail: (err) => {
        this.addDebugInfo('error', '[DD] 菜单设置失败', err)
      }
    })
  }

  /**
   * 设置导航栏返回按钮
   */
  setBackButton(callback) {
    if (!this.isDingTalkEnv()) {
      return
    }

    if (!this.isReady) {
      this.addDebugInfo('warning', '[DD] 钉钉 JSAPI 未就绪')
      return
    }

    if (!dd.biz || !dd.biz.navigation || typeof dd.biz.navigation.setMenu !== 'function') {
      this.addDebugInfo('warning', '[DD] dd.biz.navigation.setMenu 不可用')
      return
    }

    dd.biz.navigation.setMenu({
      control: true,
      items: [
        {
          id: 'goBack',
          text: '返回'
        }
      ],
      onSuccess: (result) => {
        if (result.id === 'goBack') {
          if (callback) {
            callback()
          }
        }
      },
      onFail: (err) => {
        this.addDebugInfo('error', '[DD] 设置返回按钮失败', err)
      }
    })
  }

  /**
   * 打开地图
   */
  openLocation(options = {}) {
    if (!this.isDingTalkEnv()) {
      return
    }

    if (!this.isReady) {
      this.addDebugInfo('warning', '[DD] 钉钉 JSAPI 未就绪')
      return
    }

    if (!dd.device || !dd.device.geolocation || typeof dd.device.geolocation.get !== 'function') {
      this.addDebugInfo('warning', '[DD] dd.device.geolocation.get 不可用')
      return
    }

    dd.device.geolocation.get({
      targetAccuracy: 200,
      coordinate: 1,
      withReGeocode: true,
      onSuccess: (result) => {
        if (!dd || typeof dd.openLocation !== 'function') {
          this.addDebugInfo('warning', '[DD] dd.openLocation 不可用')
          return
        }
        
        dd.openLocation({
          longitude: options.longitude || result.longitude,
          latitude: options.latitude || result.latitude,
          name: options.name || '位置',
          address: options.address || '',
          onSuccess: () => {
            this.addDebugInfo('success', '[DD] 打开地图成功')
          },
          onFail: (err) => {
            this.addDebugInfo('error', '[DD] 打开地图失败', err)
          }
        })
      },
      onFail: (err) => {
        this.addDebugInfo('error', '[DD] 获取位置失败', err)
      }
    })
  }
}

export default new DingTalkUtil()
